// app/src/api/core/queueApi.ts

import { createQueueAxiosInstance } from "./axiosBase"
import { limitedRequest } from "./limiter"
import { addIdempotencyKey, preventDuplicateRequest } from "./idempotency"

// ===== Queue API 시스템 =====

const QUE_INTERVAL_MS = 30 // 큐 처리 간격 (밀리초)

/**
 * API 요청 큐 아이템 인터페이스
 */
interface QueueApiRequest {
  id: string
  config: any
  resolve: (value: any) => void
  reject: (error: any) => void
  timestamp: number
}

/**
 * Queue API 클래스
 * 순차적으로 API를 처리하는 큐 시스템
 */
export class QueueApiManager {
  private queue: QueueApiRequest[] = []
  private isProcessing: boolean = false
  private axiosInstance: any
  private requestCounter: number = 0

  constructor() {
    // axios 인스턴스 생성 (30초 타임아웃, 더 긴 재시도 딜레이)
    this.axiosInstance = createQueueAxiosInstance()
  }

  /**
   * 큐에 API 요청 추가
   */
  private enqueue(config: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const requestId = `queue_${++this.requestCounter}_${Date.now()}`

      const queueItem: QueueApiRequest = {
        id: requestId,
        config,
        resolve,
        reject,
        timestamp: Date.now(),
      }

      this.queue.push(queueItem)

      // console.log(`[QueueAPI] 큐에 추가: ${config.method?.toUpperCase()} ${config.url} (ID: ${requestId}, 큐 길이: ${this.queue.length})`)

      // 큐 처리 시작
      this.processQueue()
    })
  }

  /**
   * 큐 처리 (순차적으로 API 호출)
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return
    }

    this.isProcessing = true

    while (this.queue.length > 0) {
      const currentRequest = this.queue.shift()!

      try {
        // console.log(`[QueueAPI] 처리 시작: ${currentRequest.config.method?.toUpperCase()} ${currentRequest.config.url} (ID: ${currentRequest.id})`)

        // 글로벌 제한자를 통해 실제 요청 실행
        const response = await limitedRequest(() => this.axiosInstance(currentRequest.config))
        currentRequest.resolve(response)

        // console.log(`[QueueAPI] 처리 완료: ${currentRequest.id} (남은 큐: ${this.queue.length})`)

        // 다음 요청까지 약간의 딜레이 (서버 부하 감소)
        if (this.queue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, QUE_INTERVAL_MS))
        }
      } catch (error) {
        console.error(`[QueueAPI] 처리 실패: ${currentRequest.id}`, error)
        currentRequest.reject(error)
      }
    }

    this.isProcessing = false
  }

  /**
   * GET 요청
   */
  async get<T = any>(url: string, config?: any): Promise<any> {
    return this.enqueue({
      method: "GET",
      url,
      ...config,
    })
  }

  /**
   * POST 요청 (멱등키 및 중복 방지 적용)
   */
  async post<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return preventDuplicateRequest("POST", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "POST")
      return this.enqueue({
        method: "POST",
        url,
        data,
        ...configWithIdempotency,
      })
    })
  }

  /**
   * PUT 요청 (멱등키 및 중복 방지 적용)
   */
  async put<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return preventDuplicateRequest("PUT", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "PUT")
      return this.enqueue({
        method: "PUT",
        url,
        data,
        ...configWithIdempotency,
      })
    })
  }

  /**
   * PATCH 요청 (멱등키 및 중복 방지 적용)
   */
  async patch<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return preventDuplicateRequest("PATCH", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "PATCH")
      return this.enqueue({
        method: "PATCH",
        url,
        data,
        ...configWithIdempotency,
      })
    })
  }

  /**
   * DELETE 요청
   */
  async delete<T = any>(url: string, config?: any): Promise<any> {
    return this.enqueue({
      method: "DELETE",
      url,
      ...config,
    })
  }

  /**
   * 큐 상태 조회
   */
  getQueueStatus(): { length: number; isProcessing: boolean } {
    return {
      length: this.queue.length,
      isProcessing: this.isProcessing,
    }
  }

  /**
   * 큐 초기화 (모든 대기중인 요청 취소)
   */
  clearQueue(): void {
    const cancelledCount = this.queue.length

    // 모든 대기중인 요청들을 거부
    this.queue.forEach(request => {
      request.reject(new Error("Queue cleared"))
    })

    this.queue = []
  }
}

/**
 * Queue API 인스턴스 생성
 */
const queueApiManager = new QueueApiManager()

/**
 * Queue API 인스턴스 (serverApi와 동일한 인터페이스)
 */
export const queApi = {
  get: queueApiManager.get.bind(queueApiManager),
  post: queueApiManager.post.bind(queueApiManager),
  put: queueApiManager.put.bind(queueApiManager),
  patch: queueApiManager.patch.bind(queueApiManager),
  delete: queueApiManager.delete.bind(queueApiManager),

  // 인터셉터 호환성을 위한 더미 객체
  interceptors: {
    request: { use: () => {} },
    response: { use: () => {} },
  },
}

/**
 * 큐 상태 조회 함수
 */
export const getQueueStatus = () => {
  return queueApiManager.getQueueStatus()
}

/**
 * 큐 초기화 함수
 */
export const clearQueue = () => {
  queueApiManager.clearQueue()
}
